import {
  BasePayloadMapper,
  DateFormatter,
  Nullable,
  spreadIf,
  spreadIfSome,
} from '@ps/utils';

import {
  LaunchFeaturesForm,
  ScheduledLaunchForm,
  ScheduledPauseForm,
} from '../types/launch-features-form.types';
import { BillingDetailsForm } from '../types/billing-details-form.types';
import { SurveyLaunchForm } from '../types/survey-launch-form.types';
import { SurveyUrlsForm } from '../types/survey-urls-form.types';
import { UniqueLinksForm } from '../types/unique-links-form.types';
import { FilterAudienceForm } from '../types/filter-audience-form.types';
import {
  BillingDetailsPayloadDto,
  ExclusionPeriodPayloadDto,
  ScheduleLaunchInfoPayloadDto,
  ScheduleLaunchPayloadDto,
  SchedulePauseInfoPayloadDto,
  SchedulePausePayloadDto,
  SurveyLaunchDto,
  SurveyUrlsPayloadDto,
  UniqueLinksPayloadDto,
} from '../../dtos/survey-launch.dto';

export class SurveyLaunchPayloadMapper extends BasePayloadMapper<
  SurveyLaunchForm,
  SurveyLaunchDto
> {
  private readonly _surveyUrlsPayloadMapper = new SurveyUrlsPayloadMapper();
  private readonly _uniqueLinksPayloadMapper = new UniqueLinksPayloadMapper();
  private readonly _billingDetailsPayloadMapper =
    new BillingDetailsPayloadMapper();
  private readonly _scheduledLaunchPayloadMapper =
    new ScheduledLaunchPayloadMapper();
  private readonly _scheduledPausePayloadMapper =
    new ScheduledPausePayloadMapper();
  private readonly _exclusionPeriodPayloadMapper =
    new ExclusionPeriodPayloadMapper();

  public toPayload({
    surveyUrls,
    uniqueLinks,
    billingDetails,
    launchFeatures,
    filterAudience,
  }: SurveyLaunchForm): SurveyLaunchDto {
    return {
      ...this._surveyUrlsPayloadMapper.toPayload(surveyUrls),
      // Unique links own the entry point once enabled — the live URL field
      // is disabled and cleared in the form, so it must never reach the payload.
      ...spreadIf(!uniqueLinks.uniqueLinksEnabled && !!surveyUrls.liveUrl, {
        live_url: surveyUrls.liveUrl,
      }),
      ...this._billingDetailsPayloadMapper.toPayload(billingDetails),
      unique_links: this._uniqueLinksPayloadMapper.toPayload(uniqueLinks),
      schedule_launch:
        this._scheduledLaunchPayloadMapper.toPayload(launchFeatures),
      schedule_pause:
        this._scheduledPausePayloadMapper.toPayload(launchFeatures),
      ...this._exclusionPeriodPayloadMapper.toPayload(filterAudience),
    };
  }
}

// surveyUrls, uniqueLinks, billingDetails, and the Exclusion Period fields of
// filterAudience are the launch sections with a confirmed backend save
// contract, so they are the only ones written out. Add a new
// `*PayloadMapper` collaborator here, and compose it above, once another
// section has one — mirrors the per-section split LaunchFormMapper uses on
// the read side.
export class SurveyUrlsPayloadMapper extends BasePayloadMapper<
  SurveyUrlsForm,
  SurveyUrlsPayloadDto
> {
  // live_url is deliberately not mapped here — SurveyLaunchPayloadMapper is
  // its only caller, and it decides live_url's wire presence itself (it also
  // depends on uniqueLinks, which this mapper never sees). One owner, not two.
  public toPayload(surveyUrls: SurveyUrlsForm): SurveyUrlsPayloadDto {
    return {
      ...spreadIf(!!surveyUrls.testUrl, { test_url: surveyUrls.testUrl }),
      url_transform: surveyUrls.disableAutomaticVariableMapping,
    };
  }
}

export class UniqueLinksPayloadMapper extends BasePayloadMapper<
  UniqueLinksForm,
  UniqueLinksPayloadDto
> {
  public toPayload(uniqueLinks: UniqueLinksForm): UniqueLinksPayloadDto {
    return {
      enable: uniqueLinks.uniqueLinksEnabled,
      items: uniqueLinks.entryLinks,
    };
  }
}

export class ExclusionPeriodPayloadMapper extends BasePayloadMapper<
  FilterAudienceForm,
  ExclusionPeriodPayloadDto
> {
  public toPayload({
    exclusionPeriodDays,
    permanentExclusionEnabled,
    includeExcludeRespondentsEnabled,
  }: FilterAudienceForm): ExclusionPeriodPayloadDto {
    return {
      ...spreadIfSome(exclusionPeriodDays, (days) => ({
        exclusion_period: days,
      })),
      is_permanent_exclusion: permanentExclusionEnabled,
      incl_excl: includeExcludeRespondentsEnabled ? 1 : 0,
    };
  }
}

export class BillingDetailsPayloadMapper extends BasePayloadMapper<
  BillingDetailsForm,
  BillingDetailsPayloadDto
> {
  public toPayload(
    billingDetails: BillingDetailsForm,
  ): BillingDetailsPayloadDto {
    return {
      ...spreadIf(!!billingDetails.billingId, {
        billing_id: billingDetails.billingId,
      }),
      ...spreadIf(!!billingDetails.billingClient, {
        survey_client: billingDetails.billingClient,
      }),
      ...spreadIf(!!billingDetails.pmEmailsInvoice, {
        pm_emails: billingDetails.pmEmailsInvoice
          .split(',')
          .map((email) => email.trim())
          .filter(Boolean),
      }),
    };
  }
}

// Disabled ⇒ { enable: false } with info dropped entirely.
export class ScheduledLaunchPayloadMapper extends BasePayloadMapper<
  LaunchFeaturesForm,
  ScheduleLaunchPayloadDto
> {
  public toPayload(
    launchFeatures: LaunchFeaturesForm,
  ): ScheduleLaunchPayloadDto {
    if (!launchFeatures.scheduledLaunchEnabled) {
      return { enable: false };
    }
    return {
      enable: true,
      info: this._toInfo(launchFeatures.scheduledLaunch),
    };
  }

  private _toInfo(
    scheduledLaunch: ScheduledLaunchForm,
  ): ScheduleLaunchInfoPayloadDto {
    return {
      ...spreadIfSome(this._singleDate(scheduledLaunch.date), (date) => ({
        date: DateFormatter.for(date, '-').format(),
      })),
      ...spreadIfSome(this._toNumber(scheduledLaunch.hour), (hours) => ({
        hours,
      })),
      ...spreadIfSome(this._toNumber(scheduledLaunch.minute), (minutes) => ({
        minutes,
      })),
      ...spreadIf(!!scheduledLaunch.timezone, {
        timezone: scheduledLaunch.timezone,
      }),
    };
  }

  // Narrows the date picker's Date | Date[] | null value to a single Date
  private _singleDate(date: ScheduledLaunchForm['date']): Nullable<Date> {
    if (!date) {
      return null;
    }
    return Array.isArray(date) ? (date[0] ?? null) : date;
  }

  private _toNumber(value: string): Nullable<number> {
    return value === '' ? null : Number(value);
  }
}

// Disabled ⇒ { enable: false, info: [] }. Enabled ⇒ always a one-element `info` array (the one-window assumption both plans locked).
export class ScheduledPausePayloadMapper extends BasePayloadMapper<
  LaunchFeaturesForm,
  SchedulePausePayloadDto
> {
  public toPayload(
    launchFeatures: LaunchFeaturesForm,
  ): SchedulePausePayloadDto {
    if (!launchFeatures.scheduledPauseEnabled) {
      return { enable: false, info: [] };
    }
    return {
      enable: true,
      info: [this._toInfo(launchFeatures.scheduledPause)],
    };
  }

  private _toInfo(
    scheduledPause: ScheduledPauseForm,
  ): SchedulePauseInfoPayloadDto {
    return {
      days: [...scheduledPause.days],
      duration: this._toDuration(scheduledPause),
      ...spreadIf(!!scheduledPause.time, { start_time: scheduledPause.time }),
      ...spreadIf(!!scheduledPause.timezone, {
        timezone: scheduledPause.timezone,
      }),
    };
  }

  // Ensures multi-day duration resets to 0 if more than one day is selected
  private _toDuration(scheduledPause: ScheduledPauseForm): {
    days: number;
    hours: number;
    minutes: number;
  } {
    const isMultiDay = scheduledPause.days.length > 1;
    return {
      days: isMultiDay ? 0 : (scheduledPause.duration.days ?? 0),
      hours: scheduledPause.duration.hours ?? 0,
      minutes: scheduledPause.duration.minutes ?? 0,
    };
  }
}
